import { MED_CHRON_VIEW_TABS } from "@/components/MedicalChronology/constants";
import type { MedicalChronologyContextValue } from "@/components/MedicalChronology/contexts/MedicalChronologyContext";
import { MedicalChronologyContext } from "@/components/MedicalChronology/contexts/MedicalChronologyContext";
import {
  type MedicalChronologySearchContextValue,
  MedicalChronologySearchProvider,
} from "@/components/MedicalChronology/contexts/MedicalChronologySearchContext";
import { useChronologyEditGuard } from "@/components/MedicalChronology/contexts/useChronologyEditGuard";
import type { MedChronFirmContext, MedChronViewTab } from "@/components/MedicalChronology/types";
import { CASE_STATUS } from "@/constants";
import { useAuth } from "@/contexts/AuthContext";
import { useCaseFiles } from "@/hooks/useCaseFiles";
import { useCaseReviewTracking } from "@/hooks/useCaseReviewTracking";
import { useCitationPanel } from "@/hooks/useCitationPanel";
import { useMarkCaseReviewed } from "@/hooks/useMarkCaseReviewed";
import { useMedChronServices } from "@/hooks/useMedChronServices";
import { useMedicalChronologyCaseMeta } from "@/hooks/useMedicalChronologyCaseMeta";
import { useMedicalChronologyTemplate } from "@/hooks/useMedicalChronologyTemplate";
import { useMedicalChronologyVersions } from "@/hooks/useMedicalChronologyVersions";
import { useCanEditFinishedChronology } from "@/permissions/v2/cases";
import type { MedVisitCitation } from "@/services/medChronServiceV2.types";
import { getErrorMessage, isConflictError } from "@/utils/errorUtils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

interface MedicalChronologyProviderProps {
  caseId: string;
  children: React.ReactNode;
}

export const MedicalChronologyProvider: React.FC<MedicalChronologyProviderProps> = ({
  caseId,
  children,
}) => {
  const [medChronService, medChronServiceV2] = useMedChronServices();
  const markCaseReviewedAction = useMarkCaseReviewed();
  const { firmContext, hasPermission } = useAuth();
  const navigate = useNavigate();

  useCaseReviewTracking(caseId);

  const {
    files: caseFiles,
    fileMapping,
    isLoading: caseFilesLoading,
    error: caseFilesError,
  } = useCaseFiles(caseId);

  const { caseName, physicianName, caseStatusId, loadCaseMeta, updateCaseNameLocally } =
    useMedicalChronologyCaseMeta(caseId);
  const { template, templateId, loadTemplate } = useMedicalChronologyTemplate(caseId);
  const {
    versions,
    selectedVersionId,
    currentVersion,
    isVersionActionLoading,
    fetchVersions,
    fetchVersionsAndSelectCurrent,
    setSelectedVersionId,
    updateVersionName,
    setVersionAsCurrent,
    duplicateVersion,
    deleteVersion,
  } = useMedicalChronologyVersions({ caseId, medChronService, medChronServiceV2 });
  const { isCreatingVersion, runVersionEdit } = useChronologyEditGuard({
    caseStatusId,
    versions,
    fetchVersions,
    fetchVersionsAndSelectCurrent,
  });
  const canEditFinishedChronology = useCanEditFinishedChronology(firmContext?.firmId);
  const isFinishedCaseReadOnly =
    caseStatusId === CASE_STATUS.FINISHED && !canEditFinishedChronology;

  const {
    citationPanel,
    openCitation: openCitationPanel,
    openVisitCitation: openVisitCitationPanel,
    closeCitationPanel,
    setCitationPage,
    setCitationPanelWidth,
    isFullscreen,
    toggleFullscreen,
  } = useCitationPanel({
    fileMapping,
    onFailure: () => toast.error("Unable to open citation. Please contact support."),
  });

  const [showVersionNameModal, setShowVersionNameModal] = useState(false);
  const [editingVersionName, setEditingVersionName] = useState("");
  const [initialVersionName, setInitialVersionName] = useState("");
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [activeView, setActiveView] = useState<MedChronViewTab>(MED_CHRON_VIEW_TABS.SUMMARY);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
  }, [activeView]);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsPageLoading(true);
      const [metaResult, versionsResult, templateResult] = await Promise.allSettled([
        loadCaseMeta(),
        fetchVersions(),
        loadTemplate(),
      ]);

      if (metaResult.status === "rejected") {
        console.error("Error loading case details:", metaResult.reason);
      }
      if (versionsResult.status === "rejected") {
        console.error("Error loading versions:", versionsResult.reason);
        toast.error("Error loading versions");
      }
      if (templateResult.status === "rejected") {
        console.error("Error loading template:", templateResult.reason);
      }

      const allFailed =
        metaResult.status === "rejected" &&
        versionsResult.status === "rejected" &&
        templateResult.status === "rejected";
      if (allFailed) {
        toast.error("Error loading medical chronology data");
      }

      setIsPageLoading(false);
    };

    void loadInitialData();
  }, [caseId, fetchVersions, loadCaseMeta, loadTemplate]);

  const openCitationOrToast = useCallback(
    (fileId: string, open: () => void) => {
      if (!fileId) {
        toast.error("Unable to open citation. Please contact support.");
        return;
      }
      try {
        open();
      } catch (error) {
        console.error("Error handling citation click:", error);
        closeCitationPanel();
        toast.error("Unable to open citation. Please contact support.");
      }
    },
    [closeCitationPanel]
  );

  const openCitation = useCallback(
    async (fileId: string, pageNumbers: number[], visitId?: string) =>
      openCitationOrToast(fileId, () => openCitationPanel(fileId, pageNumbers, visitId)),
    [openCitationOrToast, openCitationPanel]
  );

  const openVisitCitation = useCallback(
    async (citation: MedVisitCitation) =>
      openCitationOrToast(citation.file_id, () => openVisitCitationPanel(citation)),
    [openCitationOrToast, openVisitCitationPanel]
  );

  const saveVersionName = useCallback(async () => {
    if (!selectedVersionId) return;
    try {
      await updateVersionName(selectedVersionId, editingVersionName);
      setShowVersionNameModal(false);
      toast.success("Version name updated successfully");
    } catch (error) {
      console.error("Error updating version name:", error);
      toast.error(isConflictError(error) ? getErrorMessage(error) : "Error updating version name");
    }
  }, [editingVersionName, selectedVersionId, updateVersionName]);

  const handleSetVersionAsCurrent = useCallback(async () => {
    if (!selectedVersionId) return;
    try {
      await setVersionAsCurrent(selectedVersionId);
      toast.success("Version set as current successfully");
    } catch (error) {
      console.error("Error setting version as current:", error);
      toast.error(
        isConflictError(error) ? getErrorMessage(error) : "Error setting version as current"
      );
    }
  }, [selectedVersionId, setVersionAsCurrent]);

  const handleDuplicateVersion = useCallback(
    async (versionId: string) => {
      try {
        await duplicateVersion(versionId);
        toast.success("Version duplicated successfully");
      } catch (error) {
        console.error("Error duplicating version:", error);
        toast.error("Error duplicating version");
      }
    },
    [duplicateVersion]
  );

  const handleDeleteVersion = useCallback(
    async (versionId: string) => {
      try {
        await deleteVersion(versionId);
        toast.success("Version deleted successfully");
      } catch (error) {
        console.error("Error deleting version:", error);
        toast.error(isConflictError(error) ? getErrorMessage(error) : "Error deleting version");
      }
    },
    [deleteVersion]
  );

  const markCaseReviewed = useCallback(async () => {
    try {
      const response = await markCaseReviewedAction(caseId);
      if (response.status === "success") {
        navigate("/cases");
      }
    } catch (error) {
      console.error("Error marking case as reviewed:", error);
      toast.error("Failed to update case status");
    }
  }, [markCaseReviewedAction, caseId, navigate]);

  const openVersionNameModal = useCallback((versionName: string) => {
    setEditingVersionName(versionName);
    setInitialVersionName(versionName);
    setShowVersionNameModal(true);
  }, []);

  const closeVersionNameModal = useCallback(() => {
    setShowVersionNameModal(false);
  }, []);

  const value = useMemo<MedicalChronologyContextValue>(
    () => ({
      caseId,
      caseName,
      updateCaseName: updateCaseNameLocally,
      physicianName,
      caseStatusId,
      template,
      templateId,
      fileMapping,
      caseFiles,
      caseFilesLoading,
      caseFilesError,
      versions,
      selectedVersionId,
      currentVersion,
      isVersionActionLoading,
      setSelectedVersionId,
      openVersionNameModal,
      setVersionAsCurrent: handleSetVersionAsCurrent,
      duplicateVersion: handleDuplicateVersion,
      deleteVersion: handleDeleteVersion,
      versionNameModal: {
        open: showVersionNameModal,
        value: editingVersionName,
        initialValue: initialVersionName,
      },
      setVersionNameModalValue: setEditingVersionName,
      saveVersionName,
      closeVersionNameModal,
      isCreatingVersion,
      runVersionEdit,
      isFinishedCaseReadOnly,
      citationPanel,
      openCitation,
      openVisitCitation,
      closeCitationPanel,
      setCitationPage,
      setCitationPanelWidth,
      isFullscreen,
      toggleFullscreen,
      activeView,
      setActiveView,
      isPageLoading,
      markCaseReviewed,
      navigate,
      hasPermission,
      firmContext: firmContext as MedChronFirmContext | undefined,
    }),
    [
      activeView,
      caseFiles,
      caseFilesError,
      caseFilesLoading,
      caseId,
      caseName,
      caseStatusId,
      citationPanel,
      closeCitationPanel,
      closeVersionNameModal,
      currentVersion,
      editingVersionName,
      fileMapping,
      firmContext,
      handleDeleteVersion,
      handleDuplicateVersion,
      handleSetVersionAsCurrent,
      hasPermission,
      initialVersionName,
      isCreatingVersion,
      isFinishedCaseReadOnly,
      isFullscreen,
      isPageLoading,
      isVersionActionLoading,
      markCaseReviewed,
      navigate,
      openCitation,
      openVersionNameModal,
      openVisitCitation,
      physicianName,
      runVersionEdit,
      saveVersionName,
      selectedVersionId,
      setCitationPage,
      setCitationPanelWidth,
      setSelectedVersionId,
      showVersionNameModal,
      template,
      templateId,
      toggleFullscreen,
      updateCaseNameLocally,
      versions,
    ]
  );

  const searchValue = useMemo<MedicalChronologySearchContextValue>(
    () => ({ searchQuery, setSearchQuery, debouncedSearchQuery }),
    [debouncedSearchQuery, searchQuery]
  );

  return (
    <MedicalChronologyContext.Provider value={value}>
      <MedicalChronologySearchProvider value={searchValue}>
        {children}
      </MedicalChronologySearchProvider>
    </MedicalChronologyContext.Provider>
  );
};
